import React, { useState } from 'react';
import {
  Button,
  Input,
  Select,
  Space,
  Typography,
  Tabs,
  Popconfirm,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { SubPage } from './TelegramChatPreview';
import InlineButtonEditor, { InlineButtonLayout } from './InlineButtonEditor';


const { TextArea } = Input;
const { Text } = Typography;

interface Props {
  value?: SubPage[];
  onChange?: (v: SubPage[]) => void;
  /** 主页面的动态变量字段，用于子页面按钮的变量提示 */
  dynamicVarFields?: string[];
}

const REPLY_TYPE_ICONS: Record<string, React.ReactNode> = {
  text: <FileTextOutlined />,
  photo: <PictureOutlined />,
  video: <VideoCameraOutlined />,
};

const emptySubPage = (pageIndex: number): SubPage => ({
  pageIndex,
  title: `子页面 ${pageIndex}`,
  replyType: 'text',
  text: '',
  mediaUrl: null,
  parseMode: 'HTML',
  buttons: [],
});

const SubPagesEditor: React.FC<Props> = ({
  value = [],
  onChange,
  dynamicVarFields = [],
}) => {
  const [activeKey, setActiveKey] = useState<string>('0');

  const pages = value || [];

  const update = (next: SubPage[]) => onChange?.(next);

  const addPage = () => {
    const nextIndex = pages.length > 0
      ? Math.max(...pages.map(p => p.pageIndex)) + 1
      : 1;
    const newPage = emptySubPage(nextIndex);
    update([...pages, newPage]);
    setActiveKey(String(nextIndex));
  };

  const removePage = (pageIndex: number) => {
    const next = pages.filter(p => p.pageIndex !== pageIndex);
    update(next);
    if (String(pageIndex) === activeKey) {
      setActiveKey(next.length > 0 ? String(next[next.length - 1].pageIndex) : '0');
    }
  };

  const updatePage = (pageIndex: number, patch: Partial<SubPage>) => {
    update(pages.map(p => p.pageIndex === pageIndex ? { ...p, ...patch } : p));
  };

  // All pages including main (index 0) for navigate target options
  const allPageOptions = [
    { pageIndex: 0, title: '主页面' },
    ...pages.map(p => ({ pageIndex: p.pageIndex, title: p.title || `子页面 ${p.pageIndex}` })),
  ];

  const tabItems = pages.map(page => ({
    key: String(page.pageIndex),
    label: (
      <Space size={4}>
        {REPLY_TYPE_ICONS[page.replyType] || <FileTextOutlined />}
        <span>{page.title || `子页面 ${page.pageIndex}`}</span>
      </Space>
    ),
    children: (
      <SubPageForm
        page={page}
        allPageOptions={allPageOptions}
        dynamicVarFields={dynamicVarFields}
        onUpdate={patch => updatePage(page.pageIndex, patch)}
        onRemove={() => removePage(page.pageIndex)}
      />
    ),
  }));

  if (pages.length === 0) {
    return (
      <div style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: 16, textAlign: 'center' }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          暂无子页面。添加子页面后，可在按钮中配置"跳转到页面"类型，实现多级菜单效果。
        </Text>
        <Button icon={<PlusOutlined />} onClick={addPage} size="small">
          添加子页面
        </Button>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        type="editable-card"
        onEdit={(targetKey, action) => {
          if (action === 'add') addPage();
          if (action === 'remove') {
            const idx = Number(targetKey);
            if (!isNaN(idx)) removePage(idx);
          }
        }}
        items={tabItems}
        size="small"
        style={{ padding: '0 8px' }}
        tabBarExtraContent={
          <Button size="small" icon={<PlusOutlined />} onClick={addPage} type="dashed">
            添加子页面
          </Button>
        }
      />
    </div>
  );
};

interface SubPageFormProps {
  page: SubPage;
  allPageOptions: Array<{ pageIndex: number; title: string }>;
  dynamicVarFields: string[];
  onUpdate: (patch: Partial<SubPage>) => void;
  onRemove: () => void;
}

const SubPageForm: React.FC<SubPageFormProps> = ({
  page,
  allPageOptions,
  dynamicVarFields,
  onUpdate,
  onRemove,
}) => {
  const isMedia = page.replyType !== 'text';
  const textLabel = isMedia ? '图片/视频说明文字（Caption）' : '回复内容';

  return (
    <div style={{ padding: '8px 4px 4px' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>页面标题（仅后台显示）</Text>
          <Input
            size="small"
            placeholder="如：产品介绍、联系客服..."
            value={page.title}
            onChange={e => onUpdate({ title: e.target.value })}
            style={{ marginTop: 4 }}
          />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>回复类型</Text>
          <Select
            size="small"
            style={{ width: 110, marginTop: 4, display: 'block' }}
            value={page.replyType}
            onChange={val => onUpdate({ replyType: val })}
            options={[
              { value: 'text', label: '文本' },
              { value: 'photo', label: '图片' },
              { value: 'video', label: '视频' },
            ]}
          />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>解析模式</Text>
          <Select
            size="small"
            style={{ width: 100, marginTop: 4, display: 'block' }}
            value={page.parseMode}
            onChange={val => onUpdate({ parseMode: val })}
            options={[
              { value: 'HTML', label: 'HTML' },
              { value: 'Markdown', label: 'Markdown' },
            ]}
          />
        </div>
      </div>

      {isMedia && (
        <div style={{ marginBottom: 10 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>媒体 URL</Text>
          <Input
            size="small"
            placeholder="图片/视频的 URL 地址"
            value={page.mediaUrl || ''}
            onChange={e => onUpdate({ mediaUrl: e.target.value || null })}
            style={{ marginTop: 4 }}
          />
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{textLabel}</Text>
        <TextArea
          rows={4}
          placeholder={isMedia ? '图片说明文字（Caption），可留空' : '此页面的回复内容，支持变量'}
          value={page.text}
          onChange={e => onUpdate({ text: e.target.value })}
          style={{ marginTop: 4 }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          Inline 按钮（可配置"跳转到页面"实现多级导航）
        </Text>
        <InlineButtonEditor
          value={page.buttons}
          onChange={buttons => onUpdate({ buttons })}
          dynamicVarFields={dynamicVarFields}
          availablePages={allPageOptions}
          currentPageIndex={page.pageIndex}
        />
      </div>

      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <Popconfirm
          title="确定删除此子页面？"
          onConfirm={onRemove}
          okText="确定"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除此子页面
          </Button>
        </Popconfirm>
      </div>
    </div>
  );
};

export default SubPagesEditor;
